import { FlightsClassTypes, fareClassToApiCabinMap } from 'constants/flight'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import Modal from 'LuxKit/Modal/Modal'
import { useCallback, useMemo, useState, useEffect } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import FareClassCard from './FareClassCard'
import Caption from 'LuxKit/Typography/Caption'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import Breakpoint from 'components/Common/Breakpoint'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Tooltip from 'LuxKit/Tooltip'
import Badge from 'LuxKit/Badge'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import { useFareClassPriceDifferencesPerAdult } from 'hooks/CharterFlights/useFareClassPriceDifferences'
import { EmptyArray } from 'lib/array/arrayUtils'
import useCharterFlightJourneysForHotelDates from 'hooks/CharterFlights/useCharterFlightJourneysForHotelDates'
import { MALDIVES_CHARTER_ROUTE } from 'api/lib/flightDealMap'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import { getSkyLoungeLabel } from 'lib/flights/sharedFlightLabels'
import { isSkyLoungeCompatible } from 'lib/flights/flightUtils'
import { CHARTER_CABIN_CONTENT, LineCarIcon } from 'components/Flights/CharterFlights/charterCabinContent'

const baseFareClasses = [
  FlightsClassTypes.ECONOMY,
  FlightsClassTypes.PREMIUM,
  FlightsClassTypes.BUSINESS,
] as const

const fareClassesWithLuxRow = [
  FlightsClassTypes.ECONOMY,
  FlightsClassTypes.PREMIUM,
  FlightsClassTypes.LUXROW,
  FlightsClassTypes.BUSINESS,
] as const

interface Props {
  checkInDate?: string
  checkOutDate?: string
  occupancies?: Array<App.Occupants>
  selectedClassId: FlightsClassTypes
  allowedFareClasses?: Array<FlightsClassTypes>
}

/**
 * This is specific to the Maldives charter flights. It's not designed to be reusable for future charter flight offers.
 * Uses modal's resolve mechanism to return the selected fare class.
 */
function MaldivesCharterFlightFareClassModal({
  selectedClassId,
  checkInDate,
  checkOutDate,
  occupancies = EmptyArray,
  allowedFareClasses = EmptyArray,
}: Props) {
  const intl = useIntl()
  const modalElement = useModalElementContext<FlightsClassTypes>()

  const isLuxRowEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.luxRow)
  const isSkyLoungeCompatibleWithOccupancies = isSkyLoungeCompatible(occupancies)

  // Use local state to track selection within the modal
  const [localSelectedClass, setLocalSelectedClass] = useState<FlightsClassTypes>(selectedClassId)

  const onClassSelect = useCallback((fareClass: FlightsClassTypes) => {
    setLocalSelectedClass(fareClass)
  }, [])

  const closeModal = useCallback(() => {
    // Return the selected class via modal's resolve mechanism
    modalElement.resolve(localSelectedClass)
  }, [modalElement, localSelectedClass])

  const { fareFamilies } = useCharterFlightJourneysForHotelDates({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    checkInDate,
    checkOutDate,
    occupancies,
    fareClass: selectedClassId,
  })

  const hasDates = !!(checkInDate && checkOutDate)

  const allFareClasses = useMemo(() => {
    const baseClasses = isLuxRowEnabled ? fareClassesWithLuxRow : baseFareClasses
    const allowedClasses: ReadonlyArray<FlightsClassTypes> = allowedFareClasses.length > 0 ? allowedFareClasses : baseClasses
    return baseClasses.filter(fareClass => allowedClasses.includes(fareClass))
  }, [isLuxRowEnabled, allowedFareClasses])

  const unavailableFareClasses = useMemo<Set<FlightsClassTypes>>(() => {
    if (!hasDates || !fareFamilies?.upsellFaresPerLeg) return new Set()
    const availableCabins = new Set(
      fareFamilies.upsellFaresPerLeg.flatMap(leg => leg.map(fare => fare.cabin)),
    )
    return new Set(
      allFareClasses.filter(fareClass => {
        if (fareClass === FlightsClassTypes.ECONOMY) return false
        return !availableCabins.has(fareClassToApiCabinMap[fareClass])
      }),
    )
  }, [hasDates, fareFamilies, allFareClasses])

  const firstAvailableClass = useMemo(() => {
    return allFareClasses.find(fareClass => !unavailableFareClasses.has(fareClass))
  }, [allFareClasses, unavailableFareClasses])

  useEffect(() => {
    if (unavailableFareClasses.has(localSelectedClass) && firstAvailableClass) {
      setLocalSelectedClass(firstAvailableClass)
    }
  }, [unavailableFareClasses, localSelectedClass, firstAvailableClass])

  // Calculate price differences relative to the currently selected class (before modal opened)
  const comparisonClasses = useMemo(
    () => allFareClasses.filter(c => c !== selectedClassId),
    [selectedClassId, allFareClasses],
  )

  const priceDifferences = useFareClassPriceDifferencesPerAdult({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    baseClass: selectedClassId,
    comparisonClasses,
    checkInDate,
    checkOutDate,
    occupancies,
    selectedFareClass: selectedClassId,
  })

  const economyPriceDiff = priceDifferences[FlightsClassTypes.ECONOMY]
  const premiumPriceDiff = priceDifferences[FlightsClassTypes.PREMIUM]
  const businessPriceDiff = priceDifferences[FlightsClassTypes.BUSINESS]
  const luxRowPriceDiff = priceDifferences[FlightsClassTypes.LUXROW]

  const renderFareClassSubtitle = useCallback((fareClass: FlightsClassTypes, priceDiff: number | undefined) => {
    if (fareClass === FlightsClassTypes.ECONOMY && selectedClassId === FlightsClassTypes.ECONOMY) {
      return <Caption variant="large" weight="normal" colour="neutral-two">{intl.formatMessage({
        id: 'Jc_R3A4VsmbaqRl3aF1K',
        defaultMessage: 'Included in bundle',
        description: 'Label for economy fare class when it is included in the charter flight bundle',
      })}
      </Caption>
    }
    if (selectedClassId === fareClass) {
      return undefined
    }
    if (priceDiff === undefined) {
      return undefined
    }
    return (
      <Caption variant="large" weight="normal" colour="neutral-two">
        <Group direction="horizontal" gap={4} verticalAlign="baseline">
          <PriceRowCaption>{intl.formatMessage({
            id: 'RUJ7Gofk5bJQT5Frz6g8',
            defaultMessage: 'From',
            description: 'Pricing label prefix before fare class price difference in charter flight modal',
          })}
          </PriceRowCaption>
          <PriceRowPrice price={priceDiff} saleUnit="person" size="S" signDisplay="always" />
        </Group>
      </Caption>
    )
  }, [selectedClassId, intl])

  const baggageLabel = intl.formatMessage({
    id: 'TtmcS1O5XRDvnNWVelJl',
    defaultMessage: 'Baggage',
    description: 'Section heading for baggage allowance in charter flight fare class',
  })
  const skyLoungeLabel = getSkyLoungeLabel(intl)
  const cannotAccommodateLabel = intl.formatMessage({
    id: 'GgnmxKfmBmCjgL-Nxog0',
    defaultMessage: 'Cannot accommodate your number of passengers.',
    description: 'Error message when SkyLounge fare class cannot fit the number of passengers',
  })
  const unavailableForDatesLabel = intl.formatMessage({
    id: 'DjH0Ds8OG_MdH2o7HUWN',
    defaultMessage: 'Unavailable for selected dates.',
    description: 'Error message when a fare class is sold out or unavailable for the selected travel dates',
  })

  const shouldShowEconomy = allFareClasses.includes(FlightsClassTypes.ECONOMY)
  const shouldShowPremium = allFareClasses.includes(FlightsClassTypes.PREMIUM)
  const shouldShowBusiness = allFareClasses.includes(FlightsClassTypes.BUSINESS)
  const shouldShowSkyLounge = allFareClasses.includes(FlightsClassTypes.LUXROW)

  const renderCabinInclusions = (fareClass: FlightsClassTypes) => {
    const content = CHARTER_CABIN_CONTENT[fareClass]
    if (!content) return null

    return content.inclusions.map((amenity, i) => {
      // Business transfer gets special rendering with badge + tooltip
      if (amenity.icon === LineCarIcon) {
        return (
          <CaptionTextBlock
            key={i}
            variant="medium"
            startIcon={<amenity.icon />}
            endIcon={
              <Tooltip
                description={intl.formatMessage({
                  id: '9YhUIqIqMuGyui3siwDv',
                  defaultMessage: 'SYD/BNE must book domestic flights with their charter package to be eligible',
                  description: 'Tooltip explaining eligibility for home to airport transfers in charter flights',
                })}
                placement="top"
              >
                <LineInfoCircleIcon colour="neutral-two" size={14} />
              </Tooltip>
            }
          >
            <FormattedMessage
              id="Y-Dw8m2rFgKcyn5hy7J5"
              defaultMessage="<badge>New</badge> Home to airport transfers (max 40km) in MEL/SYD/BNE"
              description="Charter flight business amenity with New badge: home to airport transfer service"
              values={{
                badge: (chunks: React.ReactNode) => <span style={{ display: 'inline-flex', verticalAlign: 'middle' }}><Badge mode="critical" kind="string">{chunks}</Badge></span>,
              }}
            />
          </CaptionTextBlock>
        )
      }

      return (
        <CaptionTextBlock key={i} variant="medium" startIcon={<amenity.icon />}>
          {intl.formatMessage(amenity.message)}
        </CaptionTextBlock>
      )
    })
  }

  const renderCabinBaggage = (fareClass: FlightsClassTypes) => {
    const content = CHARTER_CABIN_CONTENT[fareClass]
    if (!content) return null

    return content.baggage.map((item, i) => (
      <CaptionTextBlock key={i} variant="medium" startIcon={<item.icon />}>
        {intl.formatMessage(item.message)}
      </CaptionTextBlock>
    ))
  }

  const skyLoungeDisabledMessage = !isSkyLoungeCompatibleWithOccupancies
    ? cannotAccommodateLabel
    : unavailableFareClasses.has(FlightsClassTypes.LUXROW)
      ? unavailableForDatesLabel
      : undefined

  const skyLoungeCard = isLuxRowEnabled && shouldShowSkyLounge ? <FareClassCard
    fareId={FlightsClassTypes.LUXROW}
    imageId={CHARTER_CABIN_CONTENT[FlightsClassTypes.LUXROW].imageId}
    name={skyLoungeLabel}
    subtitle={renderFareClassSubtitle(FlightsClassTypes.LUXROW, luxRowPriceDiff)}
    isSelected={localSelectedClass === FlightsClassTypes.LUXROW}
    onSelect={onClassSelect}
    disabled={!isSkyLoungeCompatibleWithOccupancies || unavailableFareClasses.has(FlightsClassTypes.LUXROW)}
    disabledMessage={skyLoungeDisabledMessage}
  >
    <VerticalSpacer gap={16}>
      <VerticalSpacer gap={8}>
        {renderCabinInclusions(FlightsClassTypes.LUXROW)}
      </VerticalSpacer>
      <VerticalSpacer gap={8}>
        <BodyText variant="small" weight="bold">{baggageLabel}</BodyText>
        {renderCabinBaggage(FlightsClassTypes.LUXROW)}
      </VerticalSpacer>
    </VerticalSpacer>
  </FareClassCard> : null

  // SkyLounge appears before Business when available, after Business when disabled
  const cards = <>
    {shouldShowEconomy && (
      <FareClassCard
        fareId={FlightsClassTypes.ECONOMY}
        imageId={CHARTER_CABIN_CONTENT[FlightsClassTypes.ECONOMY].imageId}
        name={intl.formatMessage({
          id: '9p-3l7DfrDnLESIGWplP',
          defaultMessage: 'Economy',
          description: 'Name for economy fare class in charter flight selection',
        })}
        subtitle={renderFareClassSubtitle(FlightsClassTypes.ECONOMY, economyPriceDiff)}
        isSelected={localSelectedClass === FlightsClassTypes.ECONOMY}
        onSelect={onClassSelect}
      >
        <VerticalSpacer gap={16}>
          <VerticalSpacer gap={8}>
            {renderCabinInclusions(FlightsClassTypes.ECONOMY)}
          </VerticalSpacer>
          <VerticalSpacer gap={8}>
            <BodyText variant="small" weight="bold">{baggageLabel}</BodyText>
            {renderCabinBaggage(FlightsClassTypes.ECONOMY)}
          </VerticalSpacer>
        </VerticalSpacer>
      </FareClassCard>
    )}

    {shouldShowPremium && (
      <FareClassCard
        fareId={FlightsClassTypes.PREMIUM}
        imageId={CHARTER_CABIN_CONTENT[FlightsClassTypes.PREMIUM].imageId}
        name={intl.formatMessage({
          id: 't4dJKEAFC0ufwPY-vysR',
          defaultMessage: 'Economy Plus',
          description: 'Name for economy plus fare class in charter flight selection',
        })}
        subtitle={renderFareClassSubtitle(FlightsClassTypes.PREMIUM, premiumPriceDiff)}
        isSelected={localSelectedClass === FlightsClassTypes.PREMIUM}
        onSelect={onClassSelect}
        disabled={unavailableFareClasses.has(FlightsClassTypes.PREMIUM)}
        disabledMessage={unavailableFareClasses.has(FlightsClassTypes.PREMIUM) ? unavailableForDatesLabel : undefined}
      >
        <VerticalSpacer gap={16}>
          <VerticalSpacer gap={8}>
            {renderCabinInclusions(FlightsClassTypes.PREMIUM)}
          </VerticalSpacer>
          <VerticalSpacer gap={8}>
            <BodyText variant="small" weight="bold">{baggageLabel}</BodyText>
            {renderCabinBaggage(FlightsClassTypes.PREMIUM)}
          </VerticalSpacer>
        </VerticalSpacer>
      </FareClassCard>
    )}

    {isSkyLoungeCompatibleWithOccupancies && skyLoungeCard}

    {shouldShowBusiness && (
      <FareClassCard
        fareId={FlightsClassTypes.BUSINESS}
        imageId={CHARTER_CABIN_CONTENT[FlightsClassTypes.BUSINESS].imageId}
        name={intl.formatMessage({
          id: 'AQXWJeJqqWBrbMz8el99',
          defaultMessage: 'Business',
          description: 'Name for business fare class in charter flight selection',
        })}
        subtitle={renderFareClassSubtitle(FlightsClassTypes.BUSINESS, businessPriceDiff)}
        isSelected={localSelectedClass === FlightsClassTypes.BUSINESS}
        onSelect={onClassSelect}
        disabled={unavailableFareClasses.has(FlightsClassTypes.BUSINESS)}
        disabledMessage={unavailableFareClasses.has(FlightsClassTypes.BUSINESS) ? unavailableForDatesLabel : undefined}
      >
        <VerticalSpacer gap={16}>
          <VerticalSpacer gap={8}>
            {renderCabinInclusions(FlightsClassTypes.BUSINESS)}
          </VerticalSpacer>
          <VerticalSpacer gap={8}>
            <BodyText variant="small" weight="bold">{baggageLabel}</BodyText>
            {renderCabinBaggage(FlightsClassTypes.BUSINESS)}
          </VerticalSpacer>
        </VerticalSpacer>
      </FareClassCard>
    )}

    {!isSkyLoungeCompatibleWithOccupancies && skyLoungeCard}
  </>

  return <Modal
    title={intl.formatMessage({
      id: 'cQ1XgUVc5zOpGzYRq84Q',
      defaultMessage: 'Select a class',
      description: 'Title for charter flight fare class selection modal',
    })}
    subtitle={intl.formatMessage({
      id: 'Xpek05yZHohlkLZhMZhI',
      defaultMessage: 'Maldivian · Airbus A330-200',
      description: 'Subtitle showing airline name and aircraft type in charter flight fare class modal',
    })}
    primaryActionText={intl.formatMessage({
      id: '4tS92bLuwVUI3LcE957Q',
      defaultMessage: 'Continue',
      description: 'Button text to confirm fare class selection in charter flight modal',
    })}
    onPrimaryActionClick={closeModal}
    onClose={closeModal}
    size="XL"
    height="full"
    tabletHeight="auto"
  >
    <Breakpoint max="tablet">
      <CardCarousel gap={12} snap="start" gutterSize={20}>
        {cards}
      </CardCarousel>
    </Breakpoint>

    <Breakpoint min="desktop">
      <Group direction="horizontal" gap={12} verticalAlign="stretch" horizontalAlign="stretch">
        {cards}
      </Group>
    </Breakpoint>
  </Modal>
}

export default MaldivesCharterFlightFareClassModal
